import { DEFAULT_TIME_ZONE } from "./timeline";
import type {
  Anniversary,
  AnniversaryIcon,
  AnniversaryKind,
  AnniversaryOccurrence,
  EventException,
  EventOccurrence,
  ProfileBirthday,
  RecurrenceFreq,
  ScheduleEvent,
} from "./types";

/** 記念日アイコンの選択肢。並び順・ラベルの一次情報はここだけに置く */
export const ANNIVERSARY_ICONS: { value: AnniversaryIcon; label: string }[] = [
  { value: "heart", label: "恋愛" },
  { value: "cake", label: "誕生日" },
  { value: "gift", label: "プレゼント" },
  { value: "gem", label: "婚約・結婚" },
  { value: "home", label: "同棲・入籍" },
  { value: "plane", label: "旅行" },
  { value: "camera", label: "思い出" },
  { value: "star", label: "特別な日" },
  { value: "party", label: "お祝い" },
  { value: "sparkles", label: "その他" },
];

export const WEEKDAYS_JA = ["日", "月", "火", "水", "木", "金", "土"] as const;

/** 上限回数。無期限の繰り返しでも展開が終わらないことがないようにする */
const MAX_OCCURRENCES_PER_EVENT = 3000;

function partsOf(iso: string, timeZone: string) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
}

/**
 * timestamptz を指定タイムゾーンで解釈し `YYYY-MM-DD` を返す。
 * `monthKeyOf`(timeline.ts)と同じ理由でローカル時刻を使う。
 */
export function instantDateKeyOf(iso: string, timeZone = DEFAULT_TIME_ZONE): string {
  const parts = partsOf(iso, timeZone);
  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`日時を解釈できません: ${iso}`);
  }
  return `${year}-${month}-${day}`;
}

/** timestamptz を指定タイムゾーンで解釈し `HH:MM` を返す */
export function instantTimeOf(iso: string, timeZone = DEFAULT_TIME_ZONE): string {
  const parts = partsOf(iso, timeZone);
  const hour = parts.find((p) => p.type === "hour")?.value;
  const minute = parts.find((p) => p.type === "minute")?.value;
  if (hour === undefined || minute === undefined) {
    throw new Error(`日時を解釈できません: ${iso}`);
  }
  return `${hour}:${minute}`;
}

/** カレンダー上の日付(タイムゾーンを持たない、y/m/dだけの値として扱う) */
export function dateKeyOf(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** `YYYY-MM-DD` からカレンダー上の日付を作る */
export function dateFromKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  if (y === undefined || m === undefined || d === undefined) {
    throw new Error(`日付の形式が不正です: ${key}`);
  }
  return new Date(y, m - 1, d);
}

/**
 * 月表示のグリッド。日曜始まり、7の倍数になるまで前後を null で埋める。
 * `month` は 0始まり(1月 = 0)。
 */
export function monthMatrix(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/** `2026-08` 形式ではなく年月の数値から見出しを作る(例: `2026年8月`) */
export function monthTitleOf(year: number, month: number): string {
  return `${year}年${month + 1}月`;
}

function stepDate(date: Date, freq: RecurrenceFreq, interval: number): Date {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  switch (freq) {
    case "daily":
      return new Date(y, m, d + interval);
    case "weekly":
      return new Date(y, m, d + 7 * interval);
    case "monthly":
      return new Date(y, m + interval, d);
    case "yearly":
      return new Date(y + interval, m, d);
  }
}

function daysBetweenKeys(fromKey: string, toKey: string): number {
  const diff = dateFromKey(toKey).getTime() - dateFromKey(fromKey).getTime();
  return Math.round(diff / 86400000);
}

function addDaysToKey(key: string, days: number): string {
  const date = dateFromKey(key);
  return dateKeyOf(new Date(date.getFullYear(), date.getMonth(), date.getDate() + days));
}

/** 予定が何日にまたがるか(単発・繰り返し共通。繰り返しの各回もこの日数ぶん続く) */
export function eventSpanDays(event: ScheduleEvent, timeZone = DEFAULT_TIME_ZONE): number {
  const firstDateKey = instantDateKeyOf(event.startsAt, timeZone);
  const lastDateKey =
    event.endsAt !== null ? instantDateKeyOf(event.endsAt, timeZone) : firstDateKey;
  return Math.max(0, daysBetweenKeys(firstDateKey, lastDateKey));
}

/**
 * `startKey` を起点に `spanDays` 日ぶん(日を跨ぐ予定用)、range と重なる日だけを積む。
 * 単発予定はここに1回、繰り返し予定は回ごとに呼ばれる。
 */
function pushDateSpan(
  occurrences: EventOccurrence[],
  event: ScheduleEvent,
  startKey: string,
  spanDays: number,
  rangeStart: string,
  rangeEnd: string,
): void {
  const endKey = addDaysToKey(startKey, spanDays);
  const overlapStart = startKey > rangeStart ? startKey : rangeStart;
  const overlapEnd = endKey < rangeEnd ? endKey : rangeEnd;
  for (let key = overlapStart; key <= overlapEnd; key = addDaysToKey(key, 1)) {
    occurrences.push({ event, date: key, startDate: startKey });
  }
}

/**
 * 予定を指定期間ぶん展開する。
 *
 * 繰り返しの各回は「規則」から都度計算し、DBには書き出さない(→ data-model.md 5章)。
 * 各回の時刻は起点(`event.startsAt`/`endsAt`)の時刻をそのまま使う
 * — `instantTimeOf`で取り出せば良いため、ここでは日付だけを列挙する。
 * `endsAt` が開始日と別日なら日を跨ぐ予定として扱い、その間の日すべてに1件ずつ出す。
 *
 * `rangeStart`/`rangeEnd` は `YYYY-MM-DD`(両端含む、JST)。
 *
 * `exceptionDates`(→ `exceptionDatesByEventId`)に載っている回は、繰り返しの
 * 展開からスキップする。「この回だけ削除」も「この回だけ差し替え」も、規則側は
 * skipするだけで良い(差し替え先は単発予定として `events` に別途含まれるため、
 * ここでの展開結果に自然に混ざる)。
 */
export function expandEventOccurrences(
  events: readonly ScheduleEvent[],
  rangeStart: string,
  rangeEnd: string,
  timeZone = DEFAULT_TIME_ZONE,
  exceptionDates?: ReadonlyMap<string, ReadonlySet<string>>,
): EventOccurrence[] {
  const occurrences: EventOccurrence[] = [];
  const rangeEndDate = dateFromKey(rangeEnd);

  for (const event of events) {
    const firstDateKey = instantDateKeyOf(event.startsAt, timeZone);
    const spanDays = eventSpanDays(event, timeZone);

    if (event.recurrenceFreq === null) {
      pushDateSpan(occurrences, event, firstDateKey, spanDays, rangeStart, rangeEnd);
      continue;
    }

    const skippedDates = exceptionDates?.get(event.id);
    let cursor = dateFromKey(firstDateKey);
    for (let i = 0; i < MAX_OCCURRENCES_PER_EVENT && cursor <= rangeEndDate; i++) {
      const key = dateKeyOf(cursor);
      if (event.recurrenceUntil !== null && key > event.recurrenceUntil) break;
      if (skippedDates?.has(key) !== true) {
        pushDateSpan(occurrences, event, key, spanDays, rangeStart, rangeEnd);
      }
      cursor = stepDate(cursor, event.recurrenceFreq, event.recurrenceInterval);
    }
  }

  return occurrences.sort(
    (a, b) => a.date.localeCompare(b.date) || a.event.startsAt.localeCompare(b.event.startsAt),
  );
}

/** `event_exceptions` を、規則側の展開でスキップすべき日付の集合に(予定IDごとに)まとめる */
export function exceptionDatesByEventId(
  exceptions: readonly EventException[],
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const exception of exceptions) {
    const set = map.get(exception.eventId);
    if (set === undefined) map.set(exception.eventId, new Set([exception.occurrenceDate]));
    else set.add(exception.occurrenceDate);
  }
  return map;
}

/**
 * 繰り返し予定の1回ぶんの開始日から、「この回だけ」編集フォームの初期の開始日・終了日を作る。
 * 時刻は起点(`event.startsAt`/`endsAt`)のものをそのまま使うため、ここでは日付だけを返す
 * (→ `instantTimeOf` と組み合わせて使う)。
 */
export function occurrenceDateRange(
  event: ScheduleEvent,
  occurrenceStartDate: string,
  timeZone = DEFAULT_TIME_ZONE,
): { date: string; endDate: string } {
  const spanDays = eventSpanDays(event, timeZone);
  return { date: occurrenceStartDate, endDate: addDaysToKey(occurrenceStartDate, spanDays) };
}

/** JSTは夏時間が無いため固定の +09:00 で組み立てられる(→ apps/web の EventSheet.tsx と同じ考え方) */
function jstInstant(dateKey: string, timeKey: string): string {
  return new Date(`${dateKey}T${timeKey}:00+09:00`).toISOString();
}

/**
 * 繰り返し予定の1回ぶんを、その回だけの単発予定として切り出す(`recurrenceFreq` を持たない
 * コピーにする)。日別シートなど「その日ぶんだけ」を書き出したい場面向け
 * (→ apps/web/src/lib/schedule/ics.ts)。単発予定はそのまま返す。
 */
export function pinOccurrenceToSingleEvent(
  occurrence: EventOccurrence,
  timeZone = DEFAULT_TIME_ZONE,
): ScheduleEvent {
  const { event, startDate } = occurrence;
  if (event.recurrenceFreq === null) return event;

  const { date, endDate } = occurrenceDateRange(event, startDate, timeZone);
  const startsAt = event.allDay
    ? jstInstant(date, "00:00")
    : jstInstant(date, instantTimeOf(event.startsAt, timeZone));
  const endsAt = event.allDay
    ? endDate !== date
      ? jstInstant(endDate, "00:00")
      : null
    : event.endsAt !== null
      ? jstInstant(endDate, instantTimeOf(event.endsAt, timeZone))
      : null;

  return {
    ...event,
    id: `${event.id}-${startDate}`,
    startsAt,
    endsAt,
    recurrenceFreq: null,
    recurrenceInterval: 1,
    recurrenceUntil: null,
  };
}

/**
 * 日を跨ぐ予定なら終了日の日付キーを返す(単発予定のみ対象。繰り返し予定は
 * 回ごとの終了日計算が複雑になりすぎるため、常に単日として扱う)。
 */
export function multiDayEndDateKey(
  event: ScheduleEvent,
  timeZone = DEFAULT_TIME_ZONE,
): string | null {
  if (event.recurrenceFreq !== null || event.endsAt === null) return null;
  const startKey = instantDateKeyOf(event.startsAt, timeZone);
  const endKey = instantDateKeyOf(event.endsAt, timeZone);
  return endKey !== startKey ? endKey : null;
}

/**
 * 記念日の次の到来日と、その回の周年・年齢。
 *
 * `today` を引数で受け取る(内部で `new Date()` を読まない)ことで、
 * 呼び出し側が日付を固定してテストできるようにしている。
 */
export function nextAnniversaryOccurrence(baseDate: string, today: Date): AnniversaryOccurrence {
  const [baseYear, baseMonth, baseDay] = baseDate.split("-").map(Number);
  if (baseYear === undefined || baseMonth === undefined || baseDay === undefined) {
    throw new Error(`日付の形式が不正です: ${baseDate}`);
  }
  const todayKey = dateKeyOf(today);
  let occ = new Date(today.getFullYear(), baseMonth - 1, baseDay);
  if (dateKeyOf(occ) < todayKey) occ = new Date(today.getFullYear() + 1, baseMonth - 1, baseDay);
  return { date: occ, count: occ.getFullYear() - baseYear };
}

/** `target` が `today` から何日後か(過去なら負の値) */
export function daysUntil(target: Date, today: Date): number {
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const d = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  return Math.round((d.getTime() - t.getTime()) / 86400000);
}

/** カウントダウン用に、近い順に並べる */
export function sortAnniversariesByCountdown(
  anniversaries: readonly Anniversary[],
  today: Date,
): { anniversary: Anniversary; occurrence: AnniversaryOccurrence; days: number }[] {
  return anniversaries
    .map((anniversary) => {
      const occurrence = nextAnniversaryOccurrence(anniversary.baseDate, today);
      return { anniversary, occurrence, days: daysUntil(occurrence.date, today) };
    })
    .sort((a, b) => a.days - b.days);
}

/**
 * その回が「節目」かどうか。記念日(anniversary)は5刻み、誕生日(birthday)は10刻み
 * (→ ideas.md「AnniversaryManager で節目の年のカードを大きく/目立つ見た目にする」)。
 * 誕生日は毎年必ず来るため、記念日と同じ5刻みだと強調の頻度が高くなりすぎる。
 *
 * 通知バッチ(→ ideas.md「記念日の節目を自動検知して通知」)でも同じ判定を使える
 * よう、日付計算と同じくここ(shared)に置いている。
 */
export function isMilestoneAnniversary(kind: AnniversaryKind, count: number): boolean {
  if (count <= 0) return false;
  return kind === "birthday" ? count % 10 === 0 : count % 5 === 0;
}

/** 記念日一覧の中で、プロフィールの誕生日から生成した合成エントリだと分かるように付ける接頭辞 */
const BIRTHDAY_ID_PREFIX = "birthday:";

export function isBirthdayDerivedId(id: string): boolean {
  return id.startsWith(BIRTHDAY_ID_PREFIX);
}

/**
 * プロフィールの誕生日(→ Settings)から、カウントダウン用の記念日エントリを作る。
 *
 * `anniversaries` テーブルには書き込まない。誕生日を変えるたびに行を同期する必要が
 * なくなり、正本は常にプロフィール1つだけになる(→ data-model.md 5章)。
 * `id` に接頭辞を付けておき、`isBirthdayDerivedId` で「編集・削除は設定画面から」の
 * 判定に使えるようにしている。
 */
export function anniversariesFromBirthdays(profiles: readonly ProfileBirthday[]): Anniversary[] {
  return profiles
    .filter((profile) => profile.birthDate !== null)
    .map((profile) => ({
      id: `${BIRTHDAY_ID_PREFIX}${profile.id}`,
      coupleId: "",
      title: `${profile.displayName}の誕生日`,
      baseDate: profile.birthDate as string,
      kind: "birthday" as const,
      icon: "cake" as const,
      memo: null,
    }));
}
